import theme from '@cubeyou-team/hx-theme';
import withSSO from '@discover-frontend/auth';
import '@discover-frontend/auth/dist/auth.css';
import { Provider as RollbarProvider } from '@rollbar/react';
import { ConnectedRouter } from 'connected-react-router';
import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import Rollbar from 'rollbar';
import { ThemeProvider } from 'styled-components';

import {
  getDocumentTitleByProductScope,
  getProductSlug,
} from '@discover-frontend/utils';
import { getMyEmail, getMyId } from 'src/utils/AuthHelperMethods';
import { TrialPaywallProvider } from 'src/modules/trial-paywall';
import translations from 'src/lang';
import './App.css';
import { MinResolutionScreen } from './components';
import { IS_LOCAL, SSO_URLS } from './constants/config';
import {
  InitContainer,
  ModalRootContainer,
  ToastRootContainer,
} from './containers';
import GlobalStyles from './global-styles';
import { ReactQueryProvider } from './providers/ReactQueryProvider';
import { Export, Home, Logout } from './routes';
import { history } from './store/configureStore';

const isExternalPath = () => {
  return window.location.pathname.startsWith('/external');
};

const rollbarConfig = {
  accessToken: '9b3419de43b14ea5996fcd06146067d7',
  captureUncaught: true,
  captureUnhandledRejections: true,
  payload: {
    environment: process.env.REACT_APP_API_ENV,
    person: {
      id: getMyId(),
      email: getMyEmail(),
    },
    client: {
      javascript: {
        code_version: '1.0.0',
        source_map_enabled: true,
      },
    },
  },
};

const rollbar = new Rollbar(rollbarConfig);
if (IS_LOCAL) {
  rollbar.configure({ enabled: false });
}

const App = ({ store, logged, singleSignOff, requestTokenRenew }) => {
  useEffect(() => {
    function handleOnBeforeUnload(event) {
      event.stopImmediatePropagation();
    }
    window.addEventListener('beforeunload', handleOnBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleOnBeforeUnload);
    };
  }, []);

  document.title = getDocumentTitleByProductScope();

  const scriptHotjar = document.createElement('script');
  const hotjar = `;(function(h, o, t, j, a, r) {
        h.hj =
          h.hj ||
          function() {
            ;(h.hj.q = h.hj.q || []).push(arguments)
          }
        h._hjSettings = { hjid: 3780531, hjsv: 6 }
        a = o.getElementsByTagName('head')[0]
        r = o.createElement('script')
        r.async = 1
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv
        a.appendChild(r)
      })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=')`;

  const scriptGtag = document.createElement('script');
  const gtag = `window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-JCNTJBNDYG');`;

  scriptHotjar.innerHTML = hotjar;
  scriptGtag.innerHTML = gtag;
  const head = document.getElementsByTagName('head')[0];
  if (process.env.REACT_APP_API_ENV === 'production') {
    head.appendChild(scriptHotjar);
    head.appendChild(scriptGtag);
  }
  return (
    <RollbarProvider instance={rollbar}>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <ReactQueryProvider>
            <TrialPaywallProvider>
              <GlobalStyles />
              <ConnectedRouter history={history}>
                <>
                  <InitContainer
                    logged={logged}
                    singleSignOff={singleSignOff}
                    requestTokenRenew={requestTokenRenew}
                    isExternalPath={isExternalPath()}
                  >
                    <Switch>
                      <Route exact path="/" component={Home} />
                      <Route
                        exact
                        path="/logout"
                        component={() => (
                          <Logout singleSignOff={singleSignOff} />
                        )}
                      />
                      <Route
                        path="/external/export/:id"
                        component={() => <Export />}
                      />
                    </Switch>
                    {logged && <ModalRootContainer />}
                    {logged && <MinResolutionScreen />}
                  </InitContainer>
                  {/* Outside InitContainer so trial lockout / bootstrap holds still show toasts. */}
                  {logged && <ToastRootContainer />}
                </>
              </ConnectedRouter>
            </TrialPaywallProvider>
          </ReactQueryProvider>
        </ThemeProvider>
      </Provider>
    </RollbarProvider>
  );
};

export const MemoizedApp = React.memo(App);

const { SESSION } = translations['en-US'];

const ssoConfig = {
  idServerBase:
    SSO_URLS[
      process.env.REACT_APP_IS_LOCAL === 'true'
        ? 'localhost'
        : process.env.REACT_APP_API_ENV
    ].REACT_APP_ID_SERVER_BASE,
  idServerBaseSignIn:
    SSO_URLS[
      process.env.REACT_APP_IS_LOCAL === 'true'
        ? 'localhost'
        : process.env.REACT_APP_API_ENV
    ].REACT_APP_ID_SERVER_AUTH,
  idTokenKey: 'HELIXA_ID_TOKEN',
  accessTokenKey: 'HELIXA_ACCESS_TOKEN',
  refreshTokenKey: 'HELIXA_REFRESH_TOKEN',
  apiEnv: process.env.REACT_APP_API_ENV,
  isExternalPath,
  sessionLockCopy: {
    title: SESSION.TITLE,
    message: SESSION.MESSAGE,
    logoutLabel: SESSION.LOGOUT,
  },
};

// Use Object.defineProperty to create a getter that calls getProductSlug() at runtime
Object.defineProperty(ssoConfig, 'appID', {
  get: () => getProductSlug(),
  enumerable: true,
  configurable: true,
});

export default withSSO(ssoConfig)(MemoizedApp);
